/**
 * The dog's pose vocabulary (ROUND8 §3): the body's stances - standing, the walk, the trot, the sit, lying dead -
 * and what the head is doing over them: `alert` (level, ears up), `sniff` (nose to the ground, sweeping: the
 * dog is CASTING for his trail) or `bark` (head thrown up, jaw working: the dog HAS him). Those two are the
 * tells the player reads, so they are as far apart as the neck allows. dogPoses.ts chooses between them.
 *
 * Every rotation is about a bone's local x unless it says otherwise: positive x tips a bone's +z end down
 * (the nose, a paw forward of the joint), so a leg swings forward on NEGATIVE x.
 */

import type { Bone } from 'three';
import { D, DOG_NOSE, DOG_REST, FORE_LEGS, FORE_LOWER, HIND_LEGS, HIND_LOWER, PAW_HEEL, PAW_TOE } from './dogRig';

export type DogBoneOf = (index: number) => Bone;

const TAU = Math.PI * 2;

/** The hind leg's rest angles: the thigh forward of the hip, the hock back - the dog's own zig-zag. */
const HIND_THIGH = -0.15;
const HIND_HOCK = 0.3;

/** Shoulder to hip along the back, metres: the lever `plant` tips the body on. */
const SPAN = 0.53;

/** The head tipped back on a neck pointing down: the muzzle meets the ground at about 70 degrees. */
const SNIFF_HEAD = -0.6;
/** Where a casting dog holds the tip of its nose, metres off the ground. */
const NOSE_DOWN = 0.04;

/** Seconds the jaw is open at each bark. */
export const BARK_OPEN_SECONDS = 0.25;

/** Every bone back to its rest offset with no rotation. */
export function resetDog(bones: readonly Bone[]): void {
  for (let i = 0; i < bones.length; i++) {
    const b = bones[i];
    const rest = DOG_REST[i];
    if (!b || !rest) continue;
    b.rotation.set(0, 0, 0, 'XYZ');
    b.position.set(rest[1], rest[2], rest[3]);
  }
}

type Pair = typeof FORE_LEGS | typeof HIND_LEGS;

/** Height of a pair's lower paw below the body bone, metres (negative), with the body pitched `pitch`. */
function lowestPaw(bone: DogBoneOf, legs: Pair, length: number, pitch: number): number {
  let lowest = Number.POSITIVE_INFINITY;
  for (const [upper, lower] of legs) {
    const hip = DOG_REST[upper];
    const knee = DOG_REST[lower];
    if (!hip || !knee) continue;
    const a1 = pitch + bone(upper).rotation.x;
    const a2 = a1 + bone(lower).rotation.x;
    const hipY = hip[2] * Math.cos(pitch) - hip[3] * Math.sin(pitch);
    // A tilted paw stands on its toe (folded back) or its heel (reaching forward), not on its middle.
    const sole = Math.max(PAW_TOE * Math.sin(a2), PAW_HEEL * Math.sin(a2));
    lowest = Math.min(lowest, hipY + knee[2] * Math.cos(a1) - length * Math.cos(a2) - sole);
  }
  return lowest;
}

/**
 * Stand the body on its paws: with every leg angle set, tip it until a forepaw and a hind paw both reach the ground,
 * then lift or drop it onto them. A pose says what the LEGS do - a crouch, a braced stance, a stride - and the back
 * finds its own slope and height from them, so no gait, head or sit ever floats a paw or sinks one.
 */
export function plant(bone: DogBoneOf): void {
  const body = bone(D.body);
  const fore = (): number => lowestPaw(bone, FORE_LEGS, FORE_LOWER, body.rotation.x);
  const hind = (): number => lowestPaw(bone, HIND_LEGS, HIND_LOWER, body.rotation.x);
  // Front paws lower than the hind ones: raise the front (negative x) by the angle that closes the gap. Tipping
  // swings the legs too, so it takes a few passes to settle.
  for (let i = 0; i < 4; i++) body.rotation.x -= Math.atan2(hind() - fore(), SPAN);
  body.position.y = -Math.min(fore(), hind());
}

/** Four square, head level, tail hanging. */
export function stand(bone: DogBoneOf): void {
  for (const [upper, lower] of HIND_LEGS) {
    bone(upper).rotation.x = HIND_THIGH;
    bone(lower).rotation.x = HIND_HOCK;
  }
  bone(D.tail).rotation.x = -0.9;
  plant(bone);
}

/**
 * The walk (four beats: left hind, left fore, right hind, right fore) and the trot (diagonal pairs), from the
 * distance covered so the paws do not slide. A leg lifts - its lower joint folds - while it swings forward.
 */
export function gait(bone: DogBoneOf, travelled: number, trot: boolean): void {
  const phase = (travelled / (trot ? 1.4 : 1.0)) * TAU;
  const swing = trot ? 0.5 : 0.36;
  const lift = trot ? 1.1 : 0.75;
  // Order: left fore, right fore, left hind, right hind.
  const offsets = trot ? [0, 0.5, 0.5, 0] : [0.25, 0.75, 0, 0.5];
  const legs = [...FORE_LEGS, ...HIND_LEGS];
  legs.forEach(([upper, lower], i) => {
    const psi = phase + (offsets[i] ?? 0) * TAU;
    const hind = i >= 2;
    bone(upper).rotation.x = (hind ? HIND_THIGH : 0) - swing * Math.sin(psi);
    bone(lower).rotation.x = (hind ? HIND_HOCK : 0) + lift * Math.max(0, Math.cos(psi));
  });
  // The back finds its own slope from the legs under it (`plant`). A trotting dog's head goes forward and its tail
  // streams; a walking one nods and swings its tail.
  bone(D.neck).rotation.x = trot ? 0.15 : 0.06 * Math.sin(phase * 2);
  bone(D.tail).rotation.set(trot ? -0.35 : -0.75, 0.25 * Math.sin(phase), 0);
  plant(bone);
}

/** Sitting: rump down, chest up, forelegs straight under the shoulders, the hind legs folded beside it. */
export function sit(bone: DogBoneOf): void {
  const pitch = -0.55;
  // The body's slope is `plant`'s to settle; starting it there, the forelegs come out upright.
  bone(D.body).rotation.x = pitch;
  for (const [upper] of FORE_LEGS) bone(upper).rotation.x = -pitch;
  // Stifles forward and up beside the chest, hocks down to the ground: both paws and the rump take the weight.
  for (const [upper, lower] of HIND_LEGS) {
    bone(upper).rotation.x = -1.2;
    bone(lower).rotation.x = 2.75;
  }
  // The neck stays upright as the body tips back: the head is where a standing dog's is, or a little higher.
  bone(D.neck).rotation.x = 0.35;
  bone(D.tail).rotation.x = -0.1;
  plant(bone);
}

/** The corpse: on its side, legs out, head along the ground. Every death ends exactly here. */
export function deadDog(bone: DogBoneOf): void {
  const body = bone(D.body);
  body.rotation.set(0, 0, Math.PI / 2);
  body.position.y = 0.15;
  const legs = [...FORE_LEGS, ...HIND_LEGS];
  legs.forEach(([upper, lower], i) => {
    // Lying on its side the legs sweep across the ground, not into it: the same local axis now does that.
    bone(upper).rotation.x = [-0.35, 0.05, 0.1, 0.45][i] ?? 0;
    bone(lower).rotation.x = [0.3, 0.6, 0.5, 0.2][i] ?? 0;
  });
  bone(D.neck).rotation.x = 0.9;
  bone(D.head).rotation.x = 0.25;
  bone(D.jaw).rotation.x = 0.15;
  bone(D.tail).rotation.x = -0.15;
}

/**
 * Nose to the ground, sweeping from side to side: the front end crouches to get it there. `sweep` is a
 * clock (seconds, or metres on the move): casting sweeps wide, tracking runs almost straight down the line.
 */
export function sniffHead(bone: DogBoneOf, sweep: number, wide: boolean): void {
  // Elbows folded, so `plant` tips the forehand down onto them with the hind paws still on the ground.
  for (const [upper, lower] of FORE_LEGS) {
    bone(upper).rotation.x += -0.71;
    bone(lower).rotation.x += 1.1;
  }
  const yaw = (wide ? 0.4 : 0.12) * Math.sin(sweep * 3.1);
  bone(D.head).rotation.set(SNIFF_HEAD, yaw, 0);
  bone(D.tail).rotation.x = Math.max(bone(D.tail).rotation.x, -0.25);
  plant(bone);
  // The neck goes down as far as it takes to put the nose on the ground, however a stride has moved the shoulders.
  bone(D.neck).rotation.x = neckToGround(bone, Math.cos(yaw)) - bone(D.body).rotation.x;
}

/**
 * The neck's turn from its rest lean, measured in the world (the body's pitch included), that brings the nose to
 * NOSE_DOWN with the body where `plant` put it. `reach` shortens the muzzle for a head turned aside. Bisection: the
 * nose only falls as the neck swings forward and down through this range.
 */
function neckToGround(bone: DogBoneOf, reach: number): number {
  const body = bone(D.body);
  const neck = DOG_REST[D.neck];
  const head = DOG_REST[D.head];
  if (!neck || !head) return 1.75;
  const p = body.rotation.x;
  const baseY = body.position.y + neck[2] * Math.cos(p) - neck[3] * Math.sin(p);
  const noseY = (n: number): number => {
    const h = n + SNIFF_HEAD;
    const [, ny, nz] = DOG_NOSE;
    return baseY + head[2] * Math.cos(n) - head[3] * Math.sin(n) + ny * Math.cos(h) - nz * reach * Math.sin(h);
  };
  let lo = 1.1;
  let hi = 2.3;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (noseY(mid) > NOSE_DOWN) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Head thrown up, forelegs braced, tail up, the jaw snapping open at each bark and hanging a little open
 * between them. `sinceBark` is seconds since it last gave tongue.
 */
export function barkHead(bone: DogBoneOf, sinceBark: number, seated: boolean): void {
  const open = sinceBark >= 0 && sinceBark < BARK_OPEN_SECONDS;
  const toss = open ? 0.15 : 0;
  if (!seated) {
    // Forelegs braced out in front, weight back on bent hocks: `plant` tips the forehand up off them.
    for (const [upper] of FORE_LEGS) bone(upper).rotation.x += -0.12;
    for (const [upper, lower] of HIND_LEGS) {
      bone(upper).rotation.x += -0.3;
      bone(lower).rotation.x += 0.5;
    }
    // Tail straight up: raised any less, it lies along the line of sight of a camera behind the dog and vanishes.
    bone(D.tail).rotation.x = 1.3;
  }
  bone(D.neck).rotation.x = (seated ? 0 : -0.35) - toss;
  bone(D.head).rotation.x = -0.5;
  bone(D.jaw).rotation.x = open ? 0.65 : 0.1;
  plant(bone);
}

/** Head level, ears up; standing still, it looks about a little. */
export function alertHead(bone: DogBoneOf, time: number, still: boolean): void {
  if (still) bone(D.head).rotation.y = 0.25 * Math.sin(time * 0.9);
}
